import { FilledIcon, OutlinedIcon } from '@one/icons';
import { PocButton, PocHeaderRow, PocTab, PocTabs, PocTypography } from 'commons/components/components-wrapper';
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { TooltipedIconComponent } from '../tooltiped-icon';
import {
    SubmoduleHeaderStyledComponent,
    SubmoduleHeaderSubTitle,
    SubmoduleHeaderSuffixContainer,
    SubmoduleHeaderTabsRow,
    SubmoduleHeaderTitleContainer,
    SubmoduleHeaderTitleInfoContainer,
    SubmoduleHeaderTitleRow,
} from './submodule-header.style';

export interface SubModuleHeaderComponentProps extends PropsWithChildren {
    className?: string;
    prefixIcon?: FilledIcon | OutlinedIcon;
    prefixTooltipText?: string;
    prefixIconTestId?: string;
    suffixButtonText?: string;
    title: React.ReactNode;
    subtitle?: string;
    caption?: string | React.ReactNode;
    suffixContent?: React.ReactNode;
    tabs?: {
        id: string;
        name: string;
    }[];
    tabsInset?: boolean;
    tabSelected?: string;
    suffixButtonAction?: VoidFunction;
    prefixIconAction?: VoidFunction;
    onChangeTab?: (tabSelected: string) => void;
}

const DEFAULT_TIME_TO_SCROLL = 100;

export const SubModuleHeaderComponent: React.FC<SubModuleHeaderComponentProps> = (props) => {
    const {
        children,
        className,
        prefixIcon,
        prefixTooltipText,
        prefixIconTestId,
        prefixIconAction,
        suffixButtonText,
        suffixButtonAction,
        suffixContent,
        title,
        subtitle,
        caption,
        tabs,
        tabsInset = false,
        tabSelected,
        onChangeTab,
    } = props;

    const tabsRefs = useRef<(HTMLOwcTabElement | null)[]>([]);

    const scrollToRef = (ref: HTMLOwcTabElement | null) =>
        setTimeout(
            () => ref?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' }),
            DEFAULT_TIME_TO_SCROLL
        );
    const scrollToPane = (num: number) => scrollToRef(tabsRefs.current[num]);

    useEffect(() => {
        if (tabs != null && tabSelected != null) {
            tabsRefs.current = tabsRefs.current.slice(0, tabs.length);
            const indexSelected = tabs.map(({ id }) => id).indexOf(tabSelected);
            scrollToPane(indexSelected);
        }
    }, []);

    let suffixElement: React.ReactNode;
    if (suffixContent) {
        suffixElement = <SubmoduleHeaderSuffixContainer>{suffixContent}</SubmoduleHeaderSuffixContainer>;
    } else if (suffixButtonText) {
        suffixElement = (
            <PocButton variant="secondary" size="medium" onClick={suffixButtonAction}>
                {suffixButtonText}
            </PocButton>
        );
    }

    return (
        <SubmoduleHeaderStyledComponent className={className}>
            <SubmoduleHeaderTitleRow variant={'secondary'}>
                <SubmoduleHeaderTitleContainer hasPrefixIcon={Boolean(prefixIcon)}>
                    {prefixIcon ? (
                        <TooltipedIconComponent
                            icon={prefixIcon}
                            tooltipText={prefixTooltipText ?? ''}
                            onClick={prefixIconAction}
                            dataTestId={prefixIconTestId}
                        />
                    ) : undefined}
                    <SubmoduleHeaderTitleInfoContainer>
                        <PocTypography element="h6" variant="title6">
                            {title}
                        </PocTypography>
                        {subtitle ? (
                            <SubmoduleHeaderSubTitle element="span" variant="subtitle1">
                                {subtitle}
                            </SubmoduleHeaderSubTitle>
                        ) : undefined}
                        {typeof caption === 'string' ? (
                            <PocTypography element="span" variant="subtitle1">
                                {caption}
                            </PocTypography>
                        ) : (
                            caption
                        )}
                    </SubmoduleHeaderTitleInfoContainer>
                </SubmoduleHeaderTitleContainer>
                {suffixElement}
            </SubmoduleHeaderTitleRow>
            {children ? <PocHeaderRow variant={'secondary'}>{children}</PocHeaderRow> : undefined}
            {tabs ? (
                <SubmoduleHeaderTabsRow variant={'secondary'} inset={tabsInset}>
                    <PocTabs size="small" value={tabSelected} onValueChange={(ev) => onChangeTab?.(ev.detail)}>
                        {tabs.map((tab, index) => (
                            <PocTab
                                ref={(el: HTMLOwcTabElement) => (tabsRefs.current[index] = el)}
                                key={`tab-${tab.id}`}
                                value={tab.id}
                                onClick={() => scrollToPane(index)}
                            >
                                {tab.name}
                            </PocTab>
                        ))}
                    </PocTabs>
                </SubmoduleHeaderTabsRow>
            ) : undefined}
        </SubmoduleHeaderStyledComponent>
    );
};
